import { useCallback, useMemo, useReducer, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type FiltersState = {
  virtualAccountNumber?: string;
};

type PaginationState = {
  includeCursor?: boolean;
  cursorBefore: string | null;
  cursorAfter: string | null;
};

export type LocalState = PaginationState & {
  filters: FiltersState;
};

type Action =
  | { type: 'setCursorBefore'; value: string | null }
  | { type: 'setCursorAfter'; value: string | null }
  | { type: 'setFilters'; value: FiltersState };

function reducer(state: LocalState, action: Action): LocalState {
  switch (action.type) {
    case 'setFilters':
      return { ...state, filters: action.value };
    case 'setCursorBefore':
      return {
        ...state,
        cursorBefore: action.value,
        cursorAfter: null,
        includeCursor: undefined
      };
    case 'setCursorAfter':
      return {
        ...state,
        cursorAfter: action.value,
        cursorBefore: null,
        includeCursor: undefined
      };
  }
}

const setCursorBefore = (value: string | null): Action => ({
  type: 'setCursorBefore',
  value
});
const setCursorAfter = (value: string | null): Action => ({
  type: 'setCursorAfter',
  value
});
const setFilters = (value: FiltersState): Action => ({
  type: 'setFilters',
  value
});

export function useLocalState(defaultState?: Partial<LocalState>) {
  const [searchParams] = useSearchParams();

  const [initialState] = useState<LocalState>({
    filters: {
      virtualAccountNumber:
        searchParams.get('virtualAccountNumber') ?? undefined
    },
    includeCursor: defaultState?.includeCursor,
    cursorBefore:
      defaultState?.cursorBefore ?? searchParams.get('cursorBefore') ?? null,
    cursorAfter:
      defaultState?.cursorAfter ?? searchParams.get('cursorAfter') ?? null
  });

  const [state, dispatch] = useReducer(reducer, initialState);

  const queryParams = useMemo(
    () => ({
      cursorAfter: state.cursorAfter,
      cursorBefore: state.cursorBefore,
      ...state.filters
    }),
    [state]
  );

  const updateCursorAfter = useCallback(
    (value: string | null) => dispatch(setCursorAfter(value)),
    []
  );

  const resetState = () => {
    dispatch(setFilters({ virtualAccountNumber: 'All' }));
  };

  return {
    state,
    queryParams,
    dispatch,
    resetState,
    setCursorBefore,
    setCursorAfter,
    updateCursorAfter,
    setFilters
  };
}
